import { useEffect, useState } from "react"
import { MealType } from "model/food/types"
import { LogMealType } from "model/food/types/base.types"
import {
  isAIScanMeal,
  isEdamaMeal,
  isMyMeal,
  isNonIngredientMeal,
} from "model/food/utils/typeGuards/mealTypeGuards"
import {
  updateEdamaNutritionWhenUnitChange,
  updateManualNutritionMealWhenPortionChange,
  updateNonIngredientEntryNutritionWhenPortionChange,
} from "features/food/helper/edamaForm"
import {
  getNutritionFromIngredients,
  updateIngredientsWhenPortionChange,
} from "features/food/helper/myFoodForm"

const useMealDetailsForm = <T extends MealType>({
  initMeal,
  mealType,
}: {
  initMeal?: T
  mealType?: LogMealType
}) => {
  const [meal, setMeal] = useState<T | undefined>()

  useEffect(() => {
    if (initMeal) {
      setMeal({
        ...initMeal,
        type: mealType,
      })
    }
  }, [initMeal, mealType])

  const onMealTypeChange = (mealType?: LogMealType) => {
    if (meal) {
      setMeal({
        ...meal,
        type: mealType,
      })
    }
  }

  const onPortionChange = (portion: number) => {
    if (!meal) return

    if (meal.isManualNutrition) {
      const nutrient = updateManualNutritionMealWhenPortionChange(meal, portion)

      if (!isNonIngredientMeal(meal)) {
        const nextIngredients = updateIngredientsWhenPortionChange({
          ingredients: meal.ingredients,
          portion: meal.portion,
          nextPortion: portion,
        })

        meal.ingredients = nextIngredients
      }

      setMeal({
        ...meal,
        ...nutrient,
        portion,
      })
      return
    }

    if (isNonIngredientMeal(meal)) {
      const nutrient = updateNonIngredientEntryNutritionWhenPortionChange(
        meal,
        portion,
      )

      setMeal({
        ...meal,
        ...nutrient,
        portion,
      })
      return
    }

    if (isMyMeal(meal)) {
      const nextIngredients = updateIngredientsWhenPortionChange({
        ingredients: meal.ingredients,
        portion: meal.portion,
        nextPortion: portion,
      })
      const nutrition = getNutritionFromIngredients(nextIngredients)

      setMeal({
        ...meal,
        ...nutrition,
        portion,
        ingredients: nextIngredients,
      })
    }

    if (isAIScanMeal(meal)) {
      const nextIngredients = updateIngredientsWhenPortionChange({
        ingredients: meal.ingredients,
        portion: meal.portion,
        nextPortion: portion,
      })
      const nutrition = getNutritionFromIngredients(nextIngredients)

      setMeal({
        ...meal,
        ...nutrition,
        portion,
        ingredients: nextIngredients,
      })
    }
  }

  const onUnitChange = (unit: string) => {
    if (!meal) return

    if (isEdamaMeal(meal)) {
      const nutrition = updateEdamaNutritionWhenUnitChange(meal, unit)
      setMeal({ ...meal, ...nutrition, unit })
    } else {
      setMeal({
        ...meal,
        unit,
      })
    }
  }

  const updateFoodAsManualNutrition = ({
    gram,
    ...nutrition
  }: Partial<MealType>) => {
    if (meal) {
      setMeal({ ...meal, ...nutrition, isManualNutrition: true })
    }
  }

  return {
    meal,
    onPortionChange,
    onUnitChange,
    onMealTypeChange,
    updateFoodAsManualNutrition,
  }
}

export default useMealDetailsForm
